import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useState,
  useReducer,
  useMemo,
} from "react";
import "./Widget.css";
import { ChatSender, SystemMessages } from "../../common/constants";
import { callEngine } from "../../api/callEngine";
import { callHuman } from "../../api/callHuman";
import { callGetPending } from "../../api/callGetPending";
import {
  DEFAULT_CONFIG,
  DEFAULT_TEXT,
  getLangOverrideKey,
  getLangOverrideValue,
  getUserConfigText,
} from "../../config";
import createRequestAttributesObject from "../../common/requestAttributes";
import BubbleWidget from "./BubbleWidget";
import InlineWidget from "./InlineWidget";
import {
  generateLinkStyles,
  isDefaultMessages,
  isDefaultSession,
} from "./utils";
import { callIntro, connectToWebsocketIntro } from "../../api/websocket";
import { mapIconUrlToGCP } from "../../common/gcp";
import { formatMessageToHtml } from "../../common/textUtils";
import { ConfigProvider, useConfig } from "./ConfigContext";
import { callCloseHuman } from "../../api/callModeSwitch";

const CHAT_STATE_PREFIX = "magicformChatState";

/**
 * @typedef {import('../../types/config').ConfigObject} ConfigObject
 * @typedef {import('../../types/config').AdvancedSizeConfig} AdvancedSizeConfig
 */

/**
 * @type React.ForwardRefRenderFunction<React.FC<any>>
 */
const WidgetController = forwardRef((_props, ref) => {
  const { config, colors, resetAllOverrides } = useConfig();
  const { patchColorOverride, patchConfigOverride } = useConfig();
  const [chatOpen, setChatOpen] = useState(false);
  // chatOpenMinimal defaults to false, a useEffect checks popUp values
  // and toggles it to true if/when necessary
  const [chatOpenMinimal, setChatOpenMinimal] = useState(false);
  const [inboundMessage, setInboundMessage] = useState("");
  /**
   * @import { SetStateAction } from "react";
   * @import { Message } from "../../types/message";
   * @type {[Message[], Dispatch<SetStateAction<Message[]>>]}
   */
  const [messages, setMessages] = useState([]);
  /**
   * @import { Session } from "../../types/session";
   * @import { SetStateAction } from "react";
   * @type {[Session, Dispatch<SetStateAction<Session>>]}
   */
  const [session, setSession] = useState({});
  const [thinking, setThinking] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [websocket, setWebsocket] = useState(null);
  const [wsConversationId, setWsConversationId] = useState(null);
  const [adminConnected, setAdminConnected] = useState(false);
  const [_, forceUpdate] = useReducer((x) => x + 1, 0);
  const [lastAction, setLastAction] = useState("None");
  const persistOpenKey = useMemo(
    () => `${config.stackId}-persistOpenState`,
    [config.stackId],
  );

  // functions exposed to the parent function via ref (callable in main.js
  // widgetComponent.current.<function>)
  useImperativeHandle(ref, () => ({
    resetWidgetContext,
    setLanguageOverride,
    resetLanguageOverride,
    toggleChatOpen,
    setChatOpenState,
    setChatContextToStorage,
    patchConfigOverride,
    patchColorOverride,
    resetAllOverrides,
  }));

  const usingWebsockets = () => {
    // return !!config.webSockets;
    return false;
  };

  const isDev = () => {
    return window.location.hostname === "localhost";
  };

  const toggleChatOpen = () => {
    // true = is open, false = closed
    const newOpenState = !chatOpen;
    setChatOpen(newOpenState);
    setChatOpenMinimal(false);
    // chatCloseOverride = true => chat is closed
    localStorage.setItem("chatCloseOverride", String(!newOpenState));
  };

  const setChatOpenState = (newOpenState) => {
    setChatOpen(newOpenState);
    localStorage.setItem("chatCloseOverride", String(!newOpenState));
  };

  const hideChat = () => {
    setChatOpen(false);
    setChatOpenMinimal(false);
    // save to local storage that chat is closed
    localStorage.setItem("chatCloseOverride", "true");
  };

  const getChatContextFromStorage = () => {
    let res = window.sessionStorage.getItem(`widget-manual-chat-context`);
    if (res) {
      res = JSON.parse(res);
      return res;
    }
  };

  const setChatContextToStorage = (context) => {
    if (context === null) {
      context = {};
    }
    window.sessionStorage.setItem(
      `widget-manual-chat-context`,
      JSON.stringify(context),
    );
  };

  const makeLeadContext = () => {
    const urlParams = new URLSearchParams(window.location.search);
    let leadContext = {};

    let prefixes = ["utm_"];

    if (config?.allowedQueryPrefixes) {
      const allowedPrefixes = config.allowedQueryPrefixes.split(",");
      if (prefixes.length > 0) {
        prefixes = prefixes.concat(allowedPrefixes);
      }
    }

    for (const [key, value] of urlParams) {
      if ([...prefixes, "utm_"].some((prefix) => key.startsWith(prefix))) {
        leadContext[key] = value;
      }
    }

    if (config?.directParams && config.directParams !== "{}") {
      const directParams = JSON.parse(config?.directParams);
      leadContext = { ...leadContext, ...directParams };
    }

    return leadContext;
  };

  const requestAttributes = () => {
    return createRequestAttributesObject(
      config.stackId,
      config.userId,
      window.location.hostname,
      getLangOverrideValue(config) ?? config.overrideLanguage,
      getChatContextFromStorage() ?? config?.chatContext ?? {},
      makeLeadContext(),
    );
  };

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    checkMobile();
    window.addEventListener("resize", checkMobile);

    return () => {
      window.removeEventListener("resize", checkMobile);
    };
  }, []);

  // handle various open/close states on init
  useEffect(() => {
    // persistOpen takes priority over persistClose, if it's set then always set
    // the chat to open initially. It's cleared on close.
    if (
      config.persistOpen &&
      sessionStorage.getItem(persistOpenKey) === "true"
    ) {
      setChatOpen(true);
      return;
    }

    // if chat was most recently closed anywhere on the site, and persistClose
    // is true, then don't show the chat automatically
    // return
    if (
      config.persistClose === true &&
      localStorage.getItem("chatCloseOverride") === "true"
    ) {
      return;
    }

    // only check for popUpTimer if autoPopUp is false (doesn't automatically pop up)
    if (!config.autoPopUp) {
      setTimeout(
        () => {
          // toggle chatOpen only after prescribed popup delay
          if (isMobile) {
            // setChatOpenMinimal(true);
          } else {
            setChatOpen(true);
          }
        },
        (config.popUpTimer ?? 0) * 1000,
      ); // config value is for seconds, timer value accepts ms
    } else {
      // autoPopUp is true, so open minimal chat immediately

      if (isMobile) {
        if (!chatOpen) {
          // setChatOpenMinimal(true);
        }
      } else {
        setChatOpen(true);
      }
    }
  }, []);

  // handling for chatOpen changes
  useEffect(() => {
    // if persistOpen is set in the config, keep the sessionStorage variable
    // synced with the state so it's restored across page reloads
    if (config?.persistOpen) {
      if (chatOpen === true) {
        sessionStorage.setItem(persistOpenKey, "true");
      } else {
        sessionStorage.removeItem(persistOpenKey);
      }
    }

    // if the user closes the chat when human mode is open, call close human
    if (chatOpen === false && session.human_mode === true) {
      callCloseHuman(session, requestAttributes()).then(() => {
        // set human mode false and clear the async_token to stop any pending
        // get-pending calls
        setSession((prev) => ({ ...prev, human_mode: false, async_token: "" }));
        renderSystemMessage(SystemMessages.humanModeEndNotification);
      });
    }
  }, [chatOpen]);

  /////////////////////
  // WEBSOCKET
  /////////////////////

  const createWebsocket = (conversationId, storedChatState) => {
    return connectToWebsocketIntro({
      onOpen: () => {
        console.log("Websocket connected");
      },
      onMessage: async (rawData) => {
        // logic for when a new message (from another party in the websocket) is received
        const response = JSON.parse(rawData);

        setWsConversationId(response.conversation_id);

        if (response.event_type === "message") {
          console.log("On message", messages, response);
          setThinking(true);
          await renderBotMessagesWebsocket(response.messages);

          // if the server says that human_mode for this conversation has
          // changed, render a system message saying so
          if (res.session.human_mode !== session.human_mode) {
            if (res.session.human_mode === false) {
              renderSystemMessage(SystemMessages.humanModeEndNotification);
            } else if (res.session.human_mode === true) {
              renderSystemMessage(SystemMessages.humanModeStartNotification);
            }
          }

          setThinking(false);
          setStoredChatState(
            messages,
            {},
            wsConversationId,
            storedChatState.convoStartDate,
          );
        } else if (response.event_type === "CONNECT") {
          if (response.session_id === storedChatState.conversationId) {
            setAdminConnected(true);
          }
        } else if (response.event_type === "DISCONNECT") {
          if (response.session_id === storedChatState.conversationId) {
            setAdminConnected(false);
          }
        }
      },
      onClose: () => {
        console.log("Websocket disconnected");
        setWebsocket(null);
      },
      sessionId: conversationId,
      stackId: config.stackId,
      domain: window.location.hostname,
    });
  };

  /////////////////////
  // HTTP
  /////////////////////

  const getConversationIdFromSession = (session) => {
    try {
      if (!session) return null;
      const convoInfoRaw = session.conversation_info;

      if (!convoInfoRaw) return null;

      var convoInfo = convoInfoRaw;
      if (typeof convoInfoRaw === "string") {
        convoInfo = JSON.parse(convoInfoRaw);
      }

      return convoInfo.conversation_id;
    } catch (err) {
      return null;
    }
  };
  const getChatStateKey = () => {
    return `${CHAT_STATE_PREFIX}_${config.stackId}`;
  };

  const getStoredChatState = () => {
    const domain = window.location.hostname;
    if (domain.includes("platform.magicform")) {
      return null;
    }

    const storedChatState = localStorage.getItem(getChatStateKey());
    if (!storedChatState) {
      return null;
    }

    const storedStateObject = JSON.parse(storedChatState);
    if (!storedStateObject) {
      return null;
    }

    const convoStartDate = new Date(storedStateObject.convoStartDate);
    const convoDate = new Date(storedStateObject.convoDate);

    const maxDayms = 1000 * 60 * 60 * 24 * config.convoDaysMax; //milliseconds
    const idleDayms = 1000 * 60 * 60 * 24 * config.convoDaysIdle; //milliseconds

    if (
      new Date(convoStartDate) < new Date(Date.now() - maxDayms) ||
      new Date(convoDate) < new Date(Date.now() - idleDayms)
    ) {
      return null;
    }

    return storedStateObject ?? null;
  };

  const setStoredChatState = (
    messages,
    session,
    conversationId,
    convoStartDate,
  ) => {
    const domain = window.location.hostname;
    if (domain.includes("platform.magicform")) {
      return;
    }

    const chatState = {
      messages,
      session,
      conversationId:
        conversationId || getConversationIdFromSession(session) || null,
      convoStartDate: convoStartDate || new Date().toISOString(),
      convoDate: new Date().toISOString(),
    };

    localStorage.setItem(getChatStateKey(), JSON.stringify(chatState));
  };

  useEffect(() => {
    // useEffect to load from existing chat state
    const storedChatState = getStoredChatState();
    if (usingWebsockets()) {
      if (storedChatState) {
        const {
          messages: storedMessages,
          conversationId: storedConversationId,
        } = storedChatState;
        setMessages(storedMessages);
        setWsConversationId(storedConversationId);
        if (!websocket) {
          if (!storedMessages.length) {
            handleIntro().then();
          } else {
            setWebsocket(
              createWebsocket(storedConversationId, storedChatState),
            );
          }
        }
      } else if (
        config.widgetType !== "bubble" ||
        (config.widgetType === "bubble" && (chatOpen || chatOpenMinimal))
      ) {
        handleIntro().then();
      }
    } else {
      if (storedChatState) {
        const { messages: storedMessages, session: storedSession } =
          storedChatState;
        setMessages(storedMessages);
        setSession(storedSession);
      }

      // if no existing chat state and
      // - widget type other than bubble
      // - widget type bubble and the chat panel is open
      // run initial behavior
      if (
        (chatOpen || chatOpenMinimal) &&
        (!storedChatState ||
          !storedChatState.messages ||
          !storedChatState.messages.length)
      ) {
        // Call handleIntro only if no messages are found in storage
        handleIntro().then(() => {});
      }
    }
  }, [chatOpen, chatOpenMinimal]);

  useEffect(() => {
    // useEffect to update chat state per message
    if (usingWebsockets()) {
      if (wsConversationId) {
        setStoredChatState(messages, session, wsConversationId, null);
      } else {
      }
    } else {
      // Save chat state to localStorage whenever messages or session changes and they are not default
      if (!isDefaultSession(session) || !isDefaultMessages(messages)) {
        const storedChatState = getStoredChatState();
        if (storedChatState) {
          const { messages: storedMessages, session: storedSession } =
            storedChatState;
          if (session !== storedSession || messages !== storedMessages) {
            setStoredChatState(messages, session, null, null);
          }
        }
      }
    }
  }, [messages, session]);

  const addNewMessage = (newMessage) => {
    setMessages((prevMessages) => {
      // do it this way so multiple setStates don't overwrite each other
      // refer to: https://reactjs.org/docs/hooks-reference.html#functional-updates
      return [...prevMessages, newMessage];
    });
  };

  /**
   *
   * @param {{sender: string, text: string}[]} asyncMessages
   */
  const renderAsyncMessages = async (asyncMessages) => {
    return Promise.all(
      asyncMessages.map(
        (messageObj) =>
          new Promise(async (resolve) => {
            const messages = [];
            const messageParts = messageObj.text.split("\n");
            messages.push(...messageParts);

            const nonEmptyMessages = messages.filter(
              (message) => message.trim() !== "",
            );

            // wait to resolve the current asyncMessages item until all of its
            // split messages have rendered
            await Promise.all(
              nonEmptyMessages.map(
                (message, i) =>
                  new Promise((resolve) => {
                    setTimeout(() => {
                      const txt = formatMessageToHtml(message);
                      addNewMessage({
                        sender: messageObj.sender,
                        text: txt,
                      });
                      resolve();
                    }, 3000 * i);
                  }),
              ),
            );
            resolve();
          }),
      ),
    );
  };

  const renderBotMessages = async (botMessages) => {
    const messages = [];
    botMessages.forEach((message) => {
      const messageParts = message.split("\n");
      messages.push(...messageParts);
    });

    const nonEmptyMessages = messages.filter(
      (message) => message.trim() !== "",
    );

    // create an array of promisified timeout calls that will render each
    // message, then wrap all of them into a single promise for easier handling
    return Promise.all(
      nonEmptyMessages.map(
        (message, i) =>
          new Promise((resolve) => {
            setTimeout(() => {
              const txt = formatMessageToHtml(message);
              addNewMessage({
                sender: ChatSender.bot,
                text: txt,
              });
              resolve();
            }, 3000 * i);
          }),
      ),
    );
  };

  const renderBotMessagesWebsocket = async (botMessages) => {
    const conversationId = botMessages[0].conversation_id;

    const messages = [];
    botMessages.forEach((message) => {
      const messageParts = message.body.split("\n");
      messages.push(...messageParts);
    });

    const nonEmptyMessages = messages.filter(
      (message) => message.trim() !== "",
    );

    // same promise handling as renderBotMessages
    return Promise.all(
      nonEmptyMessages.map(
        (message, i) =>
          new Promise((resolve) => {
            setTimeout(() => {
              const txt = formatMessageToHtml(message);
              addNewMessage({
                sender: ChatSender.bot,
                // TODO: after websockets are implemented, need to determine if sender
                // is human_agent or bot based on session.human_mode or equivalent
                text: txt,
                conversationId: conversationId,
              });
              resolve();
            }, 3000 * i);
          }),
      ),
    );
  };

  /**
   * Add a message to the list with sender=ChatSender.system
   *
   * These messages are rendered in ChatBubble as aside rows visually similar
   * to the history disclaimer.
   *
   * @param {string} message
   */
  const renderSystemMessage = (message) => {
    setMessages((prev) => [
      ...prev,
      {
        sender: ChatSender.system,
        text: message,
      },
    ]);
  };

  const getImageUrl = () => {
    const imageName = config.imageUrl;
    const url = imageName ? `${imageName}` : DEFAULT_CONFIG.imageUrl;
    return mapIconUrlToGCP(url);
  };

  const isConversationComplete = (res) => {
    return (
      res.response_attributes &&
      res.response_attributes.conversation_status &&
      res.response_attributes.conversation_status === "completed"
    );
  };

  function getConversationId(s) {
    try {
      var conversation_info = s.conversation_info;
      if (typeof s.conversation_info === "string") {
        conversation_info = JSON.parse(s.conversation_info);
      }

      return conversation_info.conversation_id;
    } catch (err) {
      return null;
    }
  }

  function createEventData(s, conversationStatus) {
    return JSON.stringify({
      conversationId: getConversationId(s),
      userId: config.userId,
      conversationStatus: conversationStatus,
    });
  }

  const sendMessage = async () => {
    const trimmedMessage = inboundMessage.trim();

    setInboundMessage("");
    if (!trimmedMessage) {
      return;
    }
    if (!session.human_mode) {
      // todo: start typing indication = toggle showTyping variable to true
      setThinking(true);
    }

    addNewMessage({
      text: trimmedMessage,
      sender: ChatSender.human,
      conversationId: wsConversationId,
    });

    if (usingWebsockets()) {
      // if we're using WS, check whether we are connected, and then send inbound message
      //console.log("Using websocket, ", websocket);
      if (websocket) {
        const websocketBody = {
          messages: [
            {
              sender: "human",
              body: trimmedMessage,
              conversation_id: wsConversationId,
            },
          ],
          event_type: "message",
          conversation_id: wsConversationId,
        };
        //console.log("Sending message to websocket: ", websocketBody);

        websocket.send(JSON.stringify(websocketBody));
        setThinking(false);
        setInboundMessage("");
      } else {
        // some error handling for no websocket connection available
      }
    } else {
      var call = callEngine;

      if (session.human_mode) {
        call = callHuman;
      }

      await call(trimmedMessage, session, requestAttributes()).then(
        async (res) => {
          if (!res.async_token || res.human_mode) {
            setThinking(false);
          }

          // render received messages before rendering system messages. Need to
          // await all of them rendering because we delay using timeouts
          await renderBotMessages(res.outbound_messages);

          // if the server says that human_mode for this conversation has
          // changed, render a system message saying so
          if (res.session.human_mode !== session.human_mode) {
            if (res.session.human_mode === false) {
              renderSystemMessage(SystemMessages.humanModeEndNotification);
            } else if (res.session.human_mode === true) {
              renderSystemMessage(SystemMessages.humanModeStartNotification);
            }
          }

          setSession(res.session);
          setStoredChatState(res.outbound_messages, res.session, null, null);
          if (isConversationComplete(res)) {
            const event = new CustomEvent("magicform:conversation:complete", {
              detail: createEventData(res.session, "completed"),
            });
            window.dispatchEvent(event);
          }
        },
      );
    }
  };

  const [isAsyncRunning, setIsAsyncRunning] = useState(false);

  useEffect(() => {
    const intervalId = setInterval(async () => {
      await asyncTimer();
    }, asyncTimeout);

    return () => clearInterval(intervalId);
  }, [session, isAsyncRunning]);

  const asyncTimeout = 3000;

  const asyncTimer = async () => {
    // do nothing, already running, a longer-than-normal poll
    if (isAsyncRunning) {
      return;
    }
    setIsAsyncRunning(true);
    if (session.async_token) {
      // we have an async token, so lets handle it and get pending messages
      await callGetPending(
        session.async_token,
        session.human_mode,
        session,
        requestAttributes(),
      )
        .then(async (res) => {
          // get-pending doesn't always return messages, so handle them
          // separately from human_mode
          if (res.messages) {
            // if we got messages back then we're done thinking
            if (res.messages.some((m) => m.text)) {
              setThinking(false);
            }
            await renderAsyncMessages(res.messages);
          }

          if (res.human_mode !== session.human_mode) {
            if (res.human_mode === false) {
              renderSystemMessage(SystemMessages.humanModeEndNotification);
            } else if (res.human_mode === true) {
              renderSystemMessage(SystemMessages.humanModeStartNotification);
            }
          }

          setSession((oldSession) => ({
            ...oldSession,
            async_token: res.async_token,
            human_mode: res.human_mode,
          }));
        })
        .catch((e) => {
          console.log("error: ", e);
        });
    }
    setIsAsyncRunning(false);
  };

  const handleIntro = async () => {
    if (usingWebsockets()) {
      // initialize a session so that we can connect to the right websocket
      const introData = await callIntro(
        config.stackId,
        window.location.hostname,
      ).then((data) => {
        console.log("Init Session, ", data);
        setWsConversationId(data.conversation_id);
        renderBotMessagesWebsocket(data.outbound_messages);
        setStoredChatState(
          data.outbound_messages,
          {},
          data.conversation_id,
          null,
        );
        return data;
      });
      const storedChatState = getStoredChatState();
      const socket = createWebsocket(
        introData.conversation_id,
        storedChatState,
      );
      setWebsocket(socket);
    } else {
      await callEngine("INTRO", {}, requestAttributes()).then((res) => {
        renderBotMessages(res.outbound_messages);
        setSession(res.session);
        setStoredChatState(res.outbound_messages, res.session, null, null);
      });
    }
  };

  const resetWidgetContext = () => {
    localStorage.setItem(getChatStateKey(), "");
    localStorage.setItem("chatCloseOverride", "");
    setMessages([]);
    setSession({});
    setWebsocket(null);
    setWsConversationId(null);
    setChatOpen(false);
    setChatOpenMinimal(false);
    setChatContextToStorage(null);
  };

  const setLanguageOverride = (langCode) => {
    document.cookie = `${getLangOverrideKey(config)}=${langCode}`;
    forceUpdate();
  };

  const resetLanguageOverride = () => {
    // delete cookie by setting its expiry date to the past
    document.cookie = `${getLangOverrideKey(config)}=; expires=Thu, 01 Jan 1970 00:00:00 UTC;`;
    forceUpdate();
  };

  const resetConversation = async () => {
    setLastAction("Reset");
    if (session.human_mode === true) {
      // if human mode is active when the user resests the conversation,
      // call to close the chat
      callCloseHuman(session, requestAttributes());
    }
    if (usingWebsockets()) {
      if (websocket) {
        websocket.close();
        setMessages([]);
        setSession({});
        setWebsocket(null);
        setWsConversationId(null);
        handleIntro().then();
      }
    } else {
      // Clear the messages and session state to start a new conversation
      setMessages([]);
      setSession({});
      // Call handleIntro to start a new conversation
      handleIntro().then();
    }
  };

  const downloadTranscript = async () => {
    const labelMap = {
      [ChatSender.bot]: "Agent",
      [ChatSender.human]: "Customer",
      [ChatSender.human_agent]: "Human Agent",
      [ChatSender.system]: "System",
    };

    const formattedConversation = messages
      .map(({ sender, text }) => {
        if (sender === ChatSender.system) {
          if (text in SystemMessages) {
            text = getUserConfigText(
              config,
              SystemMessages[text], // map system message to text key
              DEFAULT_TEXT.en[SystemMessages[text]],
            );
          }
        }

        return `${labelMap[sender]}: ${text}`;
      })
      .join("\n\n");

    const blob = new Blob([formattedConversation], { type: "text/plain" });

    const anchor = document.createElement("a");
    anchor.href = URL.createObjectURL(blob);
    anchor.download = "transcript.txt";

    // Programmatically click the anchor element to trigger the download
    anchor.click();

    // Clean up resources
    URL.revokeObjectURL(anchor.href);
  };

  const commonProps = () => {
    return {
      panel: {
        messages: messages,
        inboundMessage: inboundMessage,
        setInboundMessage: setInboundMessage,
        sendMessage: sendMessage,
        thinking: thinking,
        adminConnected: adminConnected,
      },
      chatOpen: chatOpen,
      chatOpenMinimal: chatOpenMinimal,
      toggleChatOpen: toggleChatOpen,
      hideChat: hideChat,
      getImageUrl: getImageUrl,
      resetConversation: resetConversation,
      downloadTranscript,
      debugInfo: isDev()
        ? {
            session: session,
            wsConversationId: wsConversationId,
            chatState: getStoredChatState(),
            websocketUrl: websocket ? websocket.url : "",
          }
        : {},
    };
  };

  const linkStyles = useMemo(
    () => generateLinkStyles(config, colors),
    [document.cookie, config, colors],
  );

  return (
    <>
      {linkStyles}
      {{
        bubble: () => <BubbleWidget {...commonProps()} />,
        inline: () => <InlineWidget {...commonProps()} />,
      }[config.widgetType || "bubble"]()}
    </>
  );
});

class Widget extends React.Component {
  constructor(props) {
    super(props);
    this.widgetConfigName = props.widgetConfigName;
  }

  render() {
    /**
     * @type {ConfigObject}
     */
    // @ts-ignore
    const widgetConfig = window[this.widgetConfigName || "Simple-WidgetConfig"];
    return (
      <>
        <ConfigProvider initialConfig={widgetConfig}>
          <WidgetController ref={this.props.deepRef} />
        </ConfigProvider>
      </>
    );
  }
}

export default Widget;
